import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
} from '@mui/material';

const OrganisationRequestForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postcode: '',
      country: 'UK',
      posVendor: '',
      smartMeter: '',
    },
  });

  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');

  const onSubmit = async (data) => {
    setError('');
    setInviteUrl('');
    try {
      const addressParts = [
        data.addressLine1,
        data.addressLine2,
        data.city,
        data.postcode,
        data.country,
      ]
        .filter(Boolean)
        .join(', ');

      const payload = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: addressParts,
        posVendor: data.posVendor,
        smartMeter: data.smartMeter,
      };

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';
      const response = await fetch(`${baseUrl}/api/organisation-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Request failed');
      }

      const body = await response.json().catch(() => ({}));
      if (body && body.inviteUrl) {
        setInviteUrl(body.inviteUrl);
      }
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong while saving your organisation. Please try again.');
    }
  };

  const logoUrl =
    'https://horizons-cdn.hostinger.com/f34f6b4a-5701-45b6-9778-48a500b88e4d/00c7de63c68d9d1ea880547b4c0c04a6.png';

  const cleanField = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover fieldset': {
        borderColor: 'transparent',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'transparent',
        boxShadow: '0 0 0 2px rgba(0,0,0,0.15)',
      },
      transition: 'all 0.3s ease',
      background: 'rgba(255,255,255,0.85)',
    },
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeIn 0.8s ease',
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          minHeight: { xs: 'auto', md: '80vh' },
          alignItems: 'stretch',
        }}
      >
        {/* Left: Organisation form */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            animation: 'slideUp 0.7s ease',
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: { xs: 3, md: 3 },
              width: '100%',
              maxWidth: 900,
              borderRadius: 3,
              bgcolor: 'background.paper',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 18px 45px rgba(15,23,42,0.15)',
            }}
          >
            <Box sx={{ mb: 2, textAlign: 'left' }}>
              <img
                src={logoUrl}
                alt="RTNZERO"
                style={{
                  width: 120,
                  marginBottom: 12,
                }}
              />
              <Typography variant="h6" fontWeight="bold" color="text.primary" gutterBottom>
                Register your interest
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Share a few details so we can contact you and send an invite link when your
                RTNZERO workspace is ready.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {submitted && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="body2">
                    Thanks – we&apos;ve received your organisation details.
                  </Typography>
                  {inviteUrl ? (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Invite / accept URL
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: 'break-all',
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        }}
                      >
                        {inviteUrl}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => navigator.clipboard.writeText(inviteUrl)}
                        >
                          Copy link
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          component="a"
                          href={inviteUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Our team will finish setting things up and be in touch via email with your
                      access and next steps.
                    </Typography>
                  )}
                </Box>
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Lead/contact section */}
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}
              >
                About you
              </Typography>

              <TextField
                fullWidth
                label="Business Name"
                {...register('name', { required: 'Full name is required' })}
                error={!!errors.name}
                helperText={errors.name?.message}
                sx={cleanField}
                disabled={submitted}
              />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={cleanField}
                    disabled={submitted}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone (optional)"
                    {...register('phone')}
                    sx={cleanField}
                    disabled={submitted}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address line 1"
                    {...register('addressLine1')}
                    sx={cleanField}
                    disabled={submitted}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address line 2 (optional)"
                    {...register('addressLine2')}
                    sx={cleanField}
                    disabled={submitted}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="City / Town"
                    {...register('city')}
                    sx={cleanField}
                    disabled={submitted}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Postcode"
                    {...register('postcode')}
                    sx={cleanField}
                    disabled={submitted}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Country"
                    defaultValue="UK"
                    {...register('country')}
                    sx={cleanField}
                    SelectProps={{ native: true }}
                    disabled={submitted}
                  >
                    <option value="UK">United Kingdom</option>
                    <option value="IE">Ireland</option>
                    <option value="EU">European Union</option>
                    <option value="Other">Other</option>
                  </TextField>
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="POS vendor (if you use one)"
                    {...register('posVendor')}
                    sx={cleanField}
                    disabled={submitted}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Smart meter installed?"
                    defaultValue=""
                    {...register('smartMeter')}
                    sx={cleanField}
                    SelectProps={{ native: true }}
                    disabled={submitted}
                  >
                    <option value=""></option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unknown">Not sure</option>
                  </TextField>
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting || submitted}
                sx={{
                  mt: 1,
                  mb: 1,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'black',
                  color: 'white',
                  '&:hover': {
                    background: '#333',
                  },
                }}
              >
                {isSubmitting ? 'Submitting...' : submitted ? 'Submitted' : 'Submit organisation'}
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Once you submit this form, we&apos;ll record your interest and use these details to
                send you an invite link when your RTNZERO workspace is ready.
              </Typography>
            </form>
          </Paper>
        </Grid>

        {/* Right: reserved for media / brand storytelling */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'stretch',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              borderRadius: 3,
              background:
                'radial-gradient(circle at top left, #ecfeff 0%, #f5f3ff 40%, #f9fafb 100%)',
            }}
          />
        </Grid>
      </Grid>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(25px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
    </Container>
  );
};

export default OrganisationRequestForm;

